import { useMemo, useState } from "react";
import { theme, btnPrimary, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { toDateInput } from "../lib/dateOnly";
import { Field, input, textarea, secondaryBtn } from "../pages/resilience/shared";

// The details of a technical scope (docs/TECHNICAL-SCOPES.md): what the work is, who does it,
// what it covers. One form for "New scope" (POST /technical-scopes) and for editing the details
// of an open scope (PATCH /technical-scopes/:id). The document itself is drafted or edited
// separately on the scope's page. Every id picked here is checked as the organisation's by the API.

export interface ScopeOptions {
  workTypes: Array<{ key: string; label: string; hint: string }>;
  frameworks: Array<{ id: string; label: string }>;
  sites: Array<{ id: string; name: string; kind: string | null; country: string | null }>;
  zones: Array<{ id: string; name: string; purpose: string | null; siteId: string | null }>;
  assets: Array<{ id: string; name: string; kind: string | null; criticality: string | null; zoneId: string | null; siteId: string | null }>;
  suppliers: Array<{ id: string; name: string; category: string | null }>;
}

/** The scope fields the form reads and writes (GET /technical-scopes/:id `scope`). */
export interface ScopeFields {
  title: string;
  workType: string;
  purpose: string;
  performer: string | null;
  supplierId: string | null;
  frameworkIds: string[];
  siteIds: string[];
  zoneIds: string[];
  assetIds: string[];
  inclusions: string | null;
  exclusions: string | null;
  constraints: string | null;
  /** Date-only (UTC midnight) on the way out; yyyy-mm-dd on the way in. */
  plannedStart: string | null;
  plannedEnd: string | null;
}

interface PickItem { id: string; name: string; detail?: string }

/** A searchable multi-select: chips for what is chosen, a filtered list to choose from. */
export function TechnicalScopePicker({ label, items, selected, onChange, empty, help }: {
  label: string;
  items: PickItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  empty: string;
  help?: string;
}) {
  const [q, setQ] = useState("");
  const chosen = new Set(selected);
  const byId = new Map(items.map((i) => [i.id, i]));
  const needle = q.trim().toLowerCase();
  const offered = items
    .filter((i) => !chosen.has(i.id))
    .filter((i) => !needle || i.name.toLowerCase().includes(needle) || (i.detail ?? "").toLowerCase().includes(needle))
    .slice(0, 50);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, gridColumn: "1 / -1" }}>
      <span style={{ ...text.label, marginBottom: 0 }}>{label}{selected.length > 0 ? ` (${selected.length})` : ""}</span>
      {items.length === 0 ? (
        <span style={text.hint}>{empty}</span>
      ) : (
        <>
          {selected.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {selected.map((id) => (
                <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 8px", borderRadius: 3, border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText, fontSize: text.hint.fontSize }}>
                  {byId.get(id)?.name ?? "Removed record"}
                  <button type="button" aria-label="Remove" onClick={() => onChange(selected.filter((x) => x !== id))}
                    style={{ background: "none", border: "none", color: theme.color.accentText, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              ))}
              <button type="button" style={{ ...secondaryBtn, padding: "2px 8px" }} onClick={() => onChange([])}>Clear</button>
            </div>
          )}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${items.length} ${label.toLowerCase()}`} style={input} />
          <div style={{ maxHeight: 160, overflowY: "auto", border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
            {offered.length === 0 ? (
              <div style={{ ...text.hint, padding: "6px 10px" }}>{needle ? "Nothing matches." : "Everything is chosen."}</div>
            ) : offered.map((i) => (
              <button key={i.id} type="button" onClick={() => onChange([...selected, i.id])}
                style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 10px", background: "transparent", border: "none", borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer", textAlign: "left" }}>
                <span style={{ ...text.body, color: theme.color.heading }}>{i.name}</span>
                {i.detail && <span style={text.meta}>{i.detail}</span>}
              </button>
            ))}
          </div>
          {items.length - selected.length > offered.length && !needle && <span style={text.meta}>Showing the first 50. Search to find the rest.</span>}
          {help && <span style={text.hint}>{help}</span>}
        </>
      )}
    </div>
  );
}

const nullIfBlank = (v: string) => (v.trim() ? v.trim() : null);

export function TechnicalScopeForm({ options, initial, scopeId, onSaved, onCancel }: {
  options: ScopeOptions;
  /** The scope being edited; absent for a new one. */
  initial?: ScopeFields;
  scopeId?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [workType, setWorkType] = useState(initial?.workType ?? options.workTypes[0]?.key ?? "");
  const [purpose, setPurpose] = useState(initial?.purpose ?? "");
  const [performerMode, setPerformerMode] = useState<"text" | "supplier">(initial?.supplierId ? "supplier" : "text");
  const [performer, setPerformer] = useState(initial?.performer ?? "");
  const [supplierId, setSupplierId] = useState(initial?.supplierId ?? "");
  const [frameworkIds, setFrameworkIds] = useState<string[]>(initial?.frameworkIds ?? []);
  const [siteIds, setSiteIds] = useState<string[]>(initial?.siteIds ?? []);
  const [zoneIds, setZoneIds] = useState<string[]>(initial?.zoneIds ?? []);
  const [assetIds, setAssetIds] = useState<string[]>(initial?.assetIds ?? []);
  const [inclusions, setInclusions] = useState(initial?.inclusions ?? "");
  const [exclusions, setExclusions] = useState(initial?.exclusions ?? "");
  const [constraints, setConstraints] = useState(initial?.constraints ?? "");
  const [plannedStart, setPlannedStart] = useState(toDateInput(initial?.plannedStart ?? null));
  const [plannedEnd, setPlannedEnd] = useState(toDateInput(initial?.plannedEnd ?? null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hint = options.workTypes.find((w) => w.key === workType)?.hint;
  const siteName = useMemo(() => new Map(options.sites.map((s) => [s.id, s.name])), [options.sites]);
  const zoneName = useMemo(() => new Map(options.zones.map((z) => [z.id, z.name])), [options.zones]);

  const siteItems = options.sites.map((s) => ({ id: s.id, name: s.name, detail: [s.kind, s.country].filter(Boolean).join(" · ") }));
  const zoneItems = options.zones.map((z) => ({ id: z.id, name: z.name, detail: [z.purpose, z.siteId ? siteName.get(z.siteId) : null].filter(Boolean).join(" · ") }));
  const assetItems = options.assets.map((a) => ({
    id: a.id, name: a.name,
    detail: [a.kind, a.criticality ? `${a.criticality} criticality` : null, a.zoneId ? zoneName.get(a.zoneId) : null, a.siteId ? siteName.get(a.siteId) : null].filter(Boolean).join(" · "),
  }));

  const datesWrong = !!plannedStart && !!plannedEnd && plannedEnd < plannedStart;
  const ready = title.trim().length >= 3 && purpose.trim().length >= 10 && !!workType && !datesWrong;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    const body = {
      title: title.trim(), workType, purpose: purpose.trim(),
      performer: performerMode === "text" ? nullIfBlank(performer) : null,
      supplierId: performerMode === "supplier" && supplierId ? supplierId : null,
      frameworkIds, siteIds, zoneIds, assetIds,
      inclusions: nullIfBlank(inclusions), exclusions: nullIfBlank(exclusions), constraints: nullIfBlank(constraints),
      plannedStart: plannedStart || null, plannedEnd: plannedEnd || null,
    };
    try {
      if (scopeId) {
        await api.patch(`/technical-scopes/${scopeId}`, body);
        onSaved(scopeId);
      } else {
        const r = await api.post<{ scope: { id: string } }>("/technical-scopes", body);
        onSaved(r.scope.id);
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the scope"));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, padding: 20 }}>
      <Field label="Type of work *" help={hint}>
        <select value={workType} onChange={(e) => setWorkType(e.target.value)} style={input}>
          {options.workTypes.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
        </select>
      </Field>
      <Field label="Title *">
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. External penetration test, March" style={input} />
      </Field>
      <Field label="What the work is for *" span2 help="At least 10 characters. Why the work is being done and what it should show.">
        <textarea rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={4000} style={textarea} />
      </Field>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, gridColumn: "1 / -1" }}>
        <span style={{ ...text.label, marginBottom: 0 }}>Who carries it out</span>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", ...text.body }}>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="radio" checked={performerMode === "text"} onChange={() => setPerformerMode("text")} />Name them
          </label>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: options.suppliers.length ? "pointer" : "default", opacity: options.suppliers.length ? 1 : 0.6 }}>
            <input type="radio" disabled={!options.suppliers.length} checked={performerMode === "supplier"} onChange={() => setPerformerMode("supplier")} />Pick a supplier
          </label>
        </div>
        {performerMode === "text" ? (
          <input value={performer} onChange={(e) => setPerformer(e.target.value)} maxLength={200} placeholder="e.g. the internal IT team, or the testing company's name" style={input} />
        ) : (
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={input}>
            <option value="">Choose a supplier</option>
            {options.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.category ? ` (${s.category})` : ""}</option>)}
          </select>
        )}
      </div>

      <TechnicalScopePicker label="Standards" items={options.frameworks.map((f) => ({ id: f.id, name: f.label }))} selected={frameworkIds} onChange={setFrameworkIds}
        empty="The organisation is not enrolled in any standard." help="The standards the work supports, if any." />
      <TechnicalScopePicker label="Sites" items={siteItems} selected={siteIds} onChange={setSiteIds} empty="No sites are recorded." />
      <TechnicalScopePicker label="Network zones" items={zoneItems} selected={zoneIds} onChange={setZoneIds} empty="No network zones are recorded." />
      <TechnicalScopePicker label="Assets" items={assetItems} selected={assetIds} onChange={setAssetIds} empty="No active assets are recorded." help="Pick every system, device or service the work touches." />

      <Field label="In scope" span2 help="Anything else the work covers: address ranges, applications, user accounts, premises.">
        <textarea rows={3} value={inclusions} onChange={(e) => setInclusions(e.target.value)} maxLength={6000} style={textarea} />
      </Field>
      <Field label="Out of scope" span2 help="What must not be touched.">
        <textarea rows={3} value={exclusions} onChange={(e) => setExclusions(e.target.value)} maxLength={6000} style={textarea} />
      </Field>
      <Field label="Constraints" span2 help="Testing windows, systems that must stay up, who to call if something breaks.">
        <textarea rows={3} value={constraints} onChange={(e) => setConstraints(e.target.value)} maxLength={6000} style={textarea} />
      </Field>
      <Field label="Planned start">
        <input type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} style={input} />
      </Field>
      <Field label="Planned end" help={datesWrong ? "The end is before the start." : undefined}>
        <input type="date" value={plannedEnd} min={plannedStart || undefined} onChange={(e) => setPlannedEnd(e.target.value)} style={input} />
      </Field>

      {error && <div style={{ gridColumn: "1 / -1", ...text.body, color: theme.color.dangerText }}>{error}</div>}
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button type="submit" disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.6 : 1 }}>{busy ? "Saving…" : scopeId ? "Save details" : "Create scope"}</button>
        <button type="button" style={secondaryBtn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
